/**
 * Eigene Rechte der angemeldeten Person (docs/rollensystem-plan.md §6.1,
 * `meine_rechte()` aus Migration Nr. 150).
 *
 * Ein Speicher für die ganze Sitzung wie `gesperrteKonten`: geladen beim
 * ersten Gebrauch, erneuert nach `GUELTIG_MS` — solange jemand zuhört, prüft
 * ein Takt das von selbst — oder sofort nach einer Änderung an der Matrix oder
 * an den Rollen einer Person (`aktualisiereMeineRechte`). Wechselt die
 * Anmeldung, wird der Stand verworfen; eine Antwort, die noch für die alte
 * Anmeldung unterwegs war, zählt nicht.
 *
 * Fail-open auf das heutige Verhalten: Fehlt die Funktion (Migration offen),
 * gelten die Rechte der Systemrolle (`rechteAusSystemrolle`). Dasselbe gilt,
 * solange noch keine Antwort da ist — sonst blinkten Menüpunkte beim Start
 * weg und wieder her. Ein vorübergehender Fehler (Netz) behält den letzten
 * Stand aus der Datenbank. `admin`/`super_admin` haben immer alles, auch ohne
 * Netz: so sperrt sich niemand über die Matrix aus.
 *
 * Der Speicher hält nur, was die Datenbank sagt; die Systemrolle kommt erst in
 * `rechteSicht` dazu. Die Anmeldung bleibt dieselbe, wenn das Profil (und mit
 * ihm die Systemrolle) nachlädt — dafür muss nichts neu gefragt werden.
 */
import { supabase } from '../supabaseClient';
import { ALLE_RECHTE, istRecht, rechteAusSystemrolle, type RechtSchluessel } from './katalog';
import { fehltMigration } from './rollenVerwaltung';

const GUELTIG_MS = 5 * 60 * 1000;
const TAKT_MS = 60 * 1000;

/** `db` = Antwort von `meine_rechte()`; `systemrolle` = Rückfall auf das heutige Verhalten. */
export type RechteQuelle = 'db' | 'systemrolle';

export interface MeineRechte {
    quelle: RechteQuelle;
    rechte: ReadonlySet<RechtSchluessel>;
}

/** Inhalt des Sitzungsspeichers. */
export interface RechteStand {
    /** Rechte laut Datenbank; null = Migration offen oder noch keine Antwort. */
    dbRechte: ReadonlySet<RechtSchluessel> | null;
    /** Für diese Anmeldung liegt eine Antwort vor (auch „Funktion fehlt“). */
    geladen: boolean;
}

/** Was `useRechte` liefert. */
export interface RechteSicht {
    hat: (recht: RechtSchluessel) => boolean;
    quelle: RechteQuelle;
    geladen: boolean;
}

const ALLE: ReadonlySet<RechtSchluessel> = new Set(ALLE_RECHTE);
const KEINE: ReadonlySet<RechtSchluessel> = new Set();
/** Systemrollen, deren Rechte aus den Rollen kommen (Plan §3.1). */
const INTERNE_SYSTEMROLLEN: ReadonlySet<string> = new Set(['user', 'arbeitsvorbereitung']);

const istAdmin = (systemrolle: string | null | undefined): boolean =>
    systemrolle === 'admin' || systemrolle === 'super_admin';

interface DbAntwort {
    /** null, wenn die Abfrage scheiterte. */
    rechte: ReadonlySet<RechtSchluessel> | null;
    /** Funktion oder Tabelle fehlt: endgültig Rückfall, nicht bloß ein Aussetzer. */
    fehlt: boolean;
}

/** `text[]` kommt als Array von Strings; was der Katalog nicht kennt, prüft kein Code — weg damit. */
const nurBekannte = (data: unknown): ReadonlySet<RechtSchluessel> =>
    new Set(Array.isArray(data) ? (data as unknown[]).filter(istRecht) : []);

async function frageDatenbank(): Promise<DbAntwort> {
    try {
        const { data, error } = await supabase.rpc('meine_rechte');
        if (error) return { rechte: null, fehlt: fehltMigration(error) };
        return { rechte: nurBekannte(data), fehlt: false };
    } catch {
        return { rechte: null, fehlt: false };
    }
}

/** Datenbankstand und Systemrolle zu den Rechten, die gelten. */
export const fuehreRechteZusammen = (
    systemrolle: string | null | undefined,
    dbRechte: ReadonlySet<RechtSchluessel> | null,
): MeineRechte => {
    const quelle: RechteQuelle = dbRechte ? 'db' : 'systemrolle';
    if (istAdmin(systemrolle)) return { quelle, rechte: ALLE };
    // Freelancer und unbekannte Systemrollen haben nie Rechte — auch nicht aus
    // einem Speicherstand, den dieselbe Anmeldung noch unter einer früheren
    // Systemrolle geladen hat.
    if (!systemrolle || !INTERNE_SYSTEMROLLEN.has(systemrolle)) return { quelle, rechte: KEINE };
    return { quelle, rechte: dbRechte ?? rechteAusSystemrolle(systemrolle) };
};

/** Einmal fragen, ohne Speicher. Jeder Fehler fällt auf die Systemrolle zurück. */
export async function ladeMeineRechte(systemrolle: string | null | undefined): Promise<MeineRechte> {
    const antwort = await frageDatenbank();
    return fuehreRechteZusammen(systemrolle, antwort.rechte);
}

/**
 * Die Sicht für eine Systemrolle auf einen Speicherstand. Rein — `useRechte`
 * memoisiert sie, Code außerhalb von React nimmt `meineRechteStand()`.
 */
export const rechteSicht = (s: RechteStand, systemrolle: string | null | undefined): RechteSicht => {
    const { quelle, rechte } = fuehreRechteZusammen(systemrolle, s.dbRechte);
    return { hat: (recht: RechtSchluessel) => rechte.has(recht), quelle, geladen: s.geladen };
};

// ---------------------------------------------------------------------------
// Sitzungsspeicher
// ---------------------------------------------------------------------------

const ANFANG: RechteStand = { dbRechte: null, geladen: false };

let stand: RechteStand = ANFANG;
let geladenAm = 0;
let laufend: Promise<void> | null = null;
/** Zählt Anmeldungswechsel; eine Antwort aus einer früheren zählt nicht. */
let sitzung = 0;
/** `undefined` = noch kein Auth-Ereignis gesehen. */
let angemeldetAls: string | null | undefined = undefined;
let authAbo: { unsubscribe: () => void } | null = null;
let takt: ReturnType<typeof setInterval> | null = null;
const hoerer = new Set<() => void>();

const melden = () => { for (const h of hoerer) h(); };

const gleicheMenge = (a: ReadonlySet<string> | null, b: ReadonlySet<string> | null): boolean => {
    if (a === b) return true;
    if (!a || !b || a.size !== b.size) return false;
    for (const x of a) if (!b.has(x)) return false;
    return true;
};

/**
 * Neuer Stand nur, wenn sich etwas geändert hat: jede Erneuerung alle fünf
 * Minuten zöge sonst ein Neuzeichnen an jeder Stelle nach sich, die prüft.
 */
const setzeStand = (dbRechte: ReadonlySet<RechtSchluessel> | null) => {
    if (stand.geladen && gleicheMenge(stand.dbRechte, dbRechte)) return;
    stand = { dbRechte, geladen: true };
    melden();
};

const laden = (): Promise<void> => {
    // Abgemeldet gibt es nichts zu fragen: `anon` hat kein EXECUTE auf
    // `meine_rechte()` (Nr. 150), jeder Takt schriebe 42501 ins Postgres-Log.
    // Der Takt selbst läuft weiter — App hält den Hook auch hinter dem
    // Anmeldebildschirm —, nach der Anmeldung lädt `anmeldungGewechselt`.
    if (angemeldetAls === null) return Promise.resolve();
    if (laufend) return laufend;
    const nr = sitzung;
    const lauf: Promise<void> = (async () => {
        try {
            const antwort = await frageDatenbank();
            if (nr !== sitzung) return;
            // Auch bei Fehler nicht sofort erneut fragen — sonst fragte jede
            // Stelle beim Rendern wieder an. Ein Aussetzer behält den letzten
            // Stand der Datenbank, statt Menüs umzuschalten.
            geladenAm = Date.now();
            setzeStand(antwort.rechte ?? (antwort.fehlt ? null : stand.dbRechte));
        } finally {
            if (laufend === lauf) laufend = null;
        }
    })();
    laufend = lauf;
    return lauf;
};

/** Stößt das Laden an, wenn der Stand fehlt oder älter als `GUELTIG_MS` ist. */
export const holeMeineRechte = (jetzt: number = Date.now()): void => {
    if (!laufend && jetzt - geladenAm >= GUELTIG_MS) void laden();
};

/** Sofort neu laden — nach Änderungen an der Matrix oder an den Rollen einer Person. */
export const aktualisiereMeineRechte = (): Promise<void> => {
    geladenAm = 0;
    return laufend ? laufend.then(laden) : laden();
};

/** Anmeldung gewechselt: Stand verwerfen, laufende Antwort entwerten. */
const anmeldungGewechselt = (userId: string | null) => {
    const vorher = angemeldetAls;
    angemeldetAls = userId;
    if (vorher === undefined || vorher === userId) return;
    sitzung++;
    stand = ANFANG; geladenAm = 0; laufend = null;
    melden();
    // Nicht direkt im Auth-Rückruf an Supabase: supabase-js hält dort die
    // Auth-Sperre, ein rpc darin kann hängen bleiben.
    if (userId && hoerer.size > 0) setTimeout(() => holeMeineRechte(), 0);
};

const authBeobachten = () => {
    if (authAbo) return;
    try {
        const r = supabase.auth?.onAuthStateChange?.((_ereignis: string, session: { user?: { id?: string } } | null) => {
            anmeldungGewechselt(session?.user?.id ?? null);
        });
        authAbo = r?.data?.subscription ?? null;
    } catch {
        authAbo = null;
    }
};

export const meineRechteStand = (): RechteStand => stand;

export const abonniereMeineRechte = (h: () => void): (() => void) => {
    hoerer.add(h);
    authBeobachten();
    if (!takt) takt = setInterval(() => holeMeineRechte(), TAKT_MS);
    return () => {
        hoerer.delete(h);
        if (hoerer.size === 0 && takt) { clearInterval(takt); takt = null; }
    };
};

/** Nur für Tests: Speicher auf den Anfang zurück. */
export const __setzeRechteZurueck = (): void => {
    if (takt) clearInterval(takt);
    authAbo?.unsubscribe();
    stand = ANFANG; geladenAm = 0; laufend = null; sitzung = 0;
    angemeldetAls = undefined; authAbo = null; takt = null; hoerer.clear();
};
